import FormattedMessage from 'I18n/components/FormattedMessage';
import InlineFeedback from 'ai-components-ui-library/components/feedback/InlineFeedback';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { AILoader } from 'ai-components-ui-library/components/loading/AILoader';
import HtmlSectionFormatter from 'ai-rendering-library/sections/formatters/HtmlSectionFormatter';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { useTheme } from 'foundations-theming/utils';
import { useTrackMarketplaceEvent, useUsageTracker } from 'marketplace-ui-tracking';
import { getMarketplaceInteractionEvent } from 'marketplace-ui-tracking/events/marketplace';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CALLBACK_AI_RESPONSE_QUERY as PUBLIC_CALLBACK_AI_RESPONSE_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationPublicRpc.dfc.new';
import { CALLBACK_AI_RESPONSE_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationRpc.dfc.new';
import { useAppContextQuery } from '../../hooks/useAppContextQuery';
import { stripPortalId } from '../../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FEEDBACK_HEIGHT_PX = 30;
const POLLING_INTERVAL_MS = 500;
export const POLLING_TIMEOUT_MS = 30000;
export function AiSummaryCard({
  aiAnswer,
  aiRequestId,
  gridIsSingleColumn,
  maxHeight
}) {
  var _result$data, _result$data2, _result$data3;
  const theme = useTheme();
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const usageTracker = useUsageTracker();
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const summaryRef = useRef(null);
  const history = useHistory();
  const result = useAppContextQuery({
    public: PUBLIC_CALLBACK_AI_RESPONSE_QUERY,
    app: CALLBACK_AI_RESPONSE_QUERY,
    options: {
      skip: !!aiAnswer,
      pollInterval: POLLING_INTERVAL_MS,
      variables: {
        requestId: aiRequestId
      }
    }
  });
  useEffect(() => {
    // Don't set timeout if we already have an answer
    if (aiAnswer) return;
    const timeout = setTimeout(() => {
      setHasTimedOut(true);
      result.stopPolling();
    }, POLLING_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [aiAnswer, result]);
  const successfulAnswer = aiAnswer || ((_result$data = result.data) === null || _result$data === void 0 ? void 0 : _result$data.result.aiAnswer);

  // override anchor tags to use React Router
  useEffect(() => {
    var _summaryRef$current;
    (_summaryRef$current = summaryRef.current) === null || _summaryRef$current === void 0 || _summaryRef$current.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', e => {
        trackMarketplaceEvent(getMarketplaceInteractionEvent({
          action: 'ai summary card link clicked',
          value: stripPortalId(link.href)
        }));

        // grab the path from the full URL
        const path = link.href.split('/listing')[1];

        // only override if the link is a listing
        if (path) {
          e.preventDefault();
          history.push(`/listing${path}`);
        }
      });
    });
  }, [successfulAnswer, history, trackMarketplaceEvent]);
  if (successfulAnswer) {
    result.stopPolling();
    const paddingPx = parseInt(theme.space['600'], 10);
    const borderWidthPx = parseInt(theme.borderWidth['100'], 10);
    const computedMaxHeight = maxHeight - paddingPx * 2 - borderWidthPx * 2 - FEEDBACK_HEIGHT_PX;
    return /*#__PURE__*/_jsxs(CardContainer, {
      gridIsSingleColumn: gridIsSingleColumn,
      children: [/*#__PURE__*/_jsxs(ExpandableText, {
        maxHeight: computedMaxHeight,
        children: [/*#__PURE__*/_jsx(CardHeader, {}), /*#__PURE__*/_jsx("div", {
          ref: summaryRef,
          children: /*#__PURE__*/_jsx(HtmlSectionFormatter, {
            section: {
              id: 'this-is-not-used',
              type: 'HTML',
              text: successfulAnswer,
              citations: {}
            }
          })
        })]
      }), /*#__PURE__*/_jsx(Flex, {
        flex: 1,
        alignItems: "end",
        children: /*#__PURE__*/_jsx(InlineFeedback, {
          messageId: aiRequestId,
          usageTracker: usageTracker,
          onNegativeFeedback: () => {
            trackMarketplaceEvent(getMarketplaceInteractionEvent({
              action: 'ai summary card negative feedback submitted'
            }));
          }
        })
      })]
    });
  }
  if (hasTimedOut || result.error || ((_result$data2 = result.data) === null || _result$data2 === void 0 ? void 0 : _result$data2.result.status) === 'ERRORED' || ((_result$data3 = result.data) === null || _result$data3 === void 0 ? void 0 : _result$data3.result.status) === 'NOT_FOUND') {
    result.stopPolling();
    return /*#__PURE__*/_jsxs(CardContainer, {
      gridIsSingleColumn: gridIsSingleColumn,
      children: [/*#__PURE__*/_jsx(CardHeader, {}), /*#__PURE__*/_jsx(ErrorMessage, {
        type: "badRequest"
      })]
    });
  }
  return /*#__PURE__*/_jsxs(CardContainer, {
    gridIsSingleColumn: gridIsSingleColumn,
    children: [/*#__PURE__*/_jsx(CardHeader, {}), /*#__PURE__*/_jsx(Flex, {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingBlockEnd: "800",
      children: /*#__PURE__*/_jsx(AILoader, {})
    })]
  });
}
function CardContainer({
  children,
  gridIsSingleColumn
}) {
  return /*#__PURE__*/_jsx(CardWrapper, {
    compact: true,
    gridColumn: gridIsSingleColumn ? undefined : 'span 2',
    children: /*#__PURE__*/_jsx(CardSection, {
      blockSize: "100%",
      display: "flex",
      flexDirection: "column",
      children: children
    })
  });
}
function CardHeader() {
  return /*#__PURE__*/_jsxs(Flex, {
    justifyContent: "space-between",
    marginBlockEnd: "400",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "heading-25",
      textTransform: "uppercase",
      color: "text-core-subtle",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplaceStorefront.searchResults.summarizedWithAi"
      })
    }), /*#__PURE__*/_jsx(AIBadge, {})]
  });
}